
interface Array<T> {
  /**
   * Retrives an extracted minimum value from an array.
   * @param extractor function, extracts a numeric value from an array item.
   */
  min(extractor: (item: T) => number): number;

  /**
   * Retrives an extracted maximum value from an array.
   * @param extractor function, extracts a numeric value from an array item.
   */
  max(extractor: (item: T) => number): number;

  /**
   * Retrives sum of extracted values from an array.
   * @param extractor function, extracts a numeric value from an array item. If this parameter is omitted, array items will be treated as a numeric values.
   */
  sum(extractor?: (item: T) => number): number;

  /**
   * Retrives first item of an array, which satisfied a specified filter.
   * @param filter function, filters array items.
   * @param def value to be returned if no values satisfied the filter. If this parameter is omitted, undefined will be returned.
   */
  first(filter?: (item: T) => boolean, def?: T): T;

  /**
   * Retrives last item of an array, which satisfied a specified filter.
   * @param filter function, filters array items.
   * @param def value to be returned if no values satisfied the filter. If this parameter is omitted, undefined will be returned.
   */
  last(filter?: (item: T) => boolean, def?: T): T;

  /**
   * Counts items of an array, whose satisfied a specified filter.
   * @param filter function, filters array items.
   */
  count(filter: (item: T) => boolean): number;

  /**
   * Converts each item of an array into one or more sub items and returned an array contains all of those sub items.
   * @param convertor function, converts array items into one or more sub items.
   */
  many<M>(convertor: (item: T) => M[]): M[];

  /** Returns an array consists of distincted items */
  distinct(comparer?: (item: T) => any): T[];

  /**
   * Indicates, whether an array contains a specified item or not.
   * @param item item to be tested.
   */
  contains(item: T): boolean;

  /**
   * Indicates, whether an array contains at least one of the specified items or not.
   * @param items items to be tested.
   */
  containsAny(...items: T[]): boolean;

  /**
   * Indicates, whether an array contains all of the specified items or not.
   * @param items items to be tested.
   */
  containsAll(...items: T[]): boolean;

  /**
   * Groups array items.
   * @param extractor function, which determines group key for array items.
   */
  group(extractor: (item: T) => string | number): { key: string | number, items: T[] }[];

  /**
   * Converts an array into an object, each property of which represents an array item.
   * @param key function, which determines key (property name of returned object) for array items.
   * @param value function, which determines value (property value of returned object) for array items.
   */
  lookup<M>(key: (item: T) => string | number, value: (item: T) => M): { [name: string]: M };

  /**
   * Returns a sub array, which skipped a specified number of items at the beginning of source array.
   * @param count number of items to be skipped.
   */
  skip(count: number): T[];

  /**
   * Returns a sub array, which contains a specified number of items from a specified position of source array.
   * @param count number of items to be taken.
   * @param skip number of item to be skipped from the begin of source array. If not provided, elements will be taken from the begin of the source array.
   */
  take(count: number, skip?: number): T[];

  /**
   * Returns a sub array, which contains a specified number of items from a specified position of source array.
   * @param count number of items to be taken.
   * @param skip number of item to be skipped from the end of source array. If not provided, elements will be taken from the end of the source array.
   */
  takeLast(count: number, skip?: number): T[]

  /**
   * Combines two arrays into one.
   * @param other array to be combined.
   */
  union(other: T[]): T[];

  /**
   * Removes an item from the array
   * @param item item to be removed
   */
  remove(item: T): boolean;

  /**
   * Commits an action to each element of the array then return it
   * @param action action to be committed to each element of the array
   */
  commit(action: (item: T) => void): T[];

  /**
   * Sorts an array
   * @param extractor functions, which provides values, used to compare items
   * @param mode sorting mode. If not provided, array will be sorted in ascending order
   * @param target target of the sorting operation. If not provided the sorting operation will be committed in the source array.
   */
  order(extractor: (item: T) => string | number, mode?: 'ascending' | 'descending', target?: 'source' | 'clone'): T[];  
}  

{
  Array.prototype.min = function <T>(this: T[], extractor: (item: T) => number): number {
    let result = NaN;

    this.forEach(i => {
      let value = extractor(i);
      if (isNaN(result) || result > value) result = value;
    });

    return result;
  }

  Array.prototype.max = function <T>(this: T[], extractor: (item: T) => number): number {
    let result = NaN;

    this.forEach(i => {
      let value = extractor(i);
      if (isNaN(result) || result < value) result = value;
    });

    return result;
  }

  Array.prototype.sum = function <T>(this: T[], extractor?: (item: T) => number): number {
    let sum = 0;
    this.forEach(i => sum += extractor ? extractor(i) : typeof i == 'number' ? i : NaN);
    return sum;
  }

  Array.prototype.first = function <T>(this: T[], filter?: (item: T) => boolean, def?: T): T {

    if (typeof filter == 'function') {
      for (let i = 0; i < this.length; i++) {
        if (filter(this[i])) {
          return this[i];
        }
      }
    }
    else {
      if (this.length > 0) return this[0];
    }

    return def;
  }

  Array.prototype.last = function <T>(this: T[], filter?: (item: T) => boolean, def?: T): T {

    if (typeof filter == 'function') {
      for (let i = this.length - 1; i > -1; i--) {
        if (filter(this[i])) {
          return this[i];
        }
      }
    }
    else {
      if (this.length > 0) return this[this.length - 1];
    }

    return def;
  }

  Array.prototype.count = function <T>(this: T[], filter: (item: T) => boolean, def?: T): number {
    let count: number = 0;
    this.forEach(i => count += filter(i) ? 1 : 0);
    return count;
  }

  Array.prototype.many = function <T, M>(this: T[], convertor: (item: T) => M[]): M[] {
    let results: M[] = [];
    this.forEach(i => results.push(...convertor(i)));
    return results;
  }

  Array.prototype.distinct = function <T>(this: T[], comparer?: (item: T) => any): T[] {

    let keys: any[] = [];
    let results: T[] = [];

    this.forEach(i => {

      let key = comparer ? comparer(i) : i;

      if (keys.indexOf(key) < 0) {
        keys.push(key);
        results.push(i);
      }
    });

    return results;
  }

  Array.prototype.contains = function <T>(this: T[], item: T): boolean {
    return this.indexOf(item) > -1;
  }

  Array.prototype.containsAny = function <T>(this: T[], ...items: T[]): boolean {
    for (let i of this.distinct()) {
      if (items.contains(i)) return true;
    }

    return false;
  }

  Array.prototype.containsAll = function <T>(this: T[], ...items: T[]): boolean {
    for (let i of items.distinct()) {
      if (!this.contains(i)) return false;
    }

    return true;
  }

  Array.prototype.group = function <T>(this: T[], extractor: (item: T) => string | number): { key: string | number, items: T[] }[] {
    let results: { key: string | number, items: T[] }[] = [];
    let lu = {};

    this.forEach(i => {
      let key = extractor(i);

      if (!lu[key]) {
        lu[key] = { key: key, items: [] };
        results.push(lu[key]);
      }

      lu[key].items.push(i);
    });

    return results;
  }

  Array.prototype.lookup = function <T, M>(this: T[], key: (item: T) => string | number, value: (item: T) => M): { [name: string]: M } {
    let result: { [name: string]: M } = {};
    this.forEach(i => result[key(i)] = value(i));
    return result;
  }

  Array.prototype.skip = function <T>(this: T[], count: number): T[] {
    let result: T[] = [];
    for (let i = count; i < this.length; i++) result.push(this[i]);
    return result;
  }

  Array.prototype.take = function <T>(this: T[], count: number, skip?: number): T[] {
    let result: T[] = [];
    if (!skip) skip = 0;

    for (let i = Math.max(0, skip); i < Math.min(this.length, count + skip); i++) result.push(this[i]);
    return result;
  }

  Array.prototype.takeLast = function <T>(this: T[], count: number, skip?: number): T[] {
    let result: T[] = [];
    if (!skip) skip = 0;

    for (let i = Math.max(this.length - skip - count, 0); i < Math.min(this.length, this.length - skip); i++) result.push(this[i]);
    return result;
  }

  Array.prototype.union = function <T>(this: T[], other: T[]): T[] {
    let results: T[] = [];
    results.push(...this);
    results.push(...other);

    return results;
  }

  Array.prototype.remove = function <T>(this: T[], item: T): boolean {
    let index = this.indexOf(item);

    if (index > -1) {
      this.splice(index, 1);
      return true;
    }

    return false;
  }

  Array.prototype.commit = function <T>(this: T[], action: (item: T) => void): T[] {
    this.forEach(i => action(i));
    return this;
  }

  Array.prototype.order = function <T>(this: T[], extractor: (item: T) => string | number, mode?: 'ascending' | 'descending', target?: 'source' | 'clone'): T[] {
    if (!mode) mode = 'ascending';
    if (!target) target = 'source';

    let result = target == 'source' ? this : [...this];

    let sorter = mode == 'ascending'

      ? (a, b) => {
        a = extractor(a);
        b = extractor(b);

        if (typeof a == 'string' || typeof b == 'string') {
          return (typeof a == 'string' ? a : a.toString()).localeCompare(typeof b == 'string' ? b : b.toString(), 'vi');
        }
        else {
          return a > b ? 1 : a < b ? -1 : 0;
        }
      }

      : (a, b) => {

        a = extractor(a);
        b = extractor(b);

        if (typeof a == 'string' || typeof b == 'string') {
          return -(typeof a == 'string' ? a : a.toString()).localeCompare(typeof b == 'string' ? b : b.toString(), 'vi');
        }
        else {
          return a > b ? -1 : a < b ? 1 : 0;
        }
      };

    return result.sort(sorter);
  }
}

interface Date {
  /** Returns first moment of current date. */
  date(): Date;

  /** Returns first moment of current month. */
  beginOfMonth(): Date;

  /** Returns first moment of current year. */
  beginOfYear(): Date;

  /**
   * Returns a Date object which added a specified days.
   * @param days number of days to be added. Negative is allowed.
   */
  addDays(days: number): Date;

  /**
   * Returns a Date object which added a specified months.
   * @param months number of months to be added. Negative is allowed.
   */
  addMonths(months: number): Date;

  /**
   * Returns a Date object which added a specified years.
   * @param years number of years to be added. Negative is allowed.
   */
  addYears(years: number): Date;
}

{
  Date.prototype.date = function (this: Date): Date {
    return new Date(this.getFullYear(), this.getMonth(), this.getDate(), 0, 0, 0, 0);
  }

  Date.prototype.beginOfMonth = function (this: Date): Date {
    let date = new Date(this.getTime());
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    return date.date();
  }

  Date.prototype.beginOfYear = function (this: Date): Date {
    let date = new Date(this.getTime());
    date.setDate(1);
    date.setMonth(0);
    date.setHours(0, 0, 0, 0);
    return date.date();
  }

  Date.prototype.addDays = function (this: Date, days: number): Date {
    return new Date(this.getTime() + days * 24 * 60 * 60000);
  }

  Date.prototype.addMonths = function (this: Date, months: number): Date {
    let date = new Date(this.getTime());
    date.setMonth(this.getMonth() + months);
    return date;
  }

  Date.prototype.addYears = function (this: Date, years: number): Date {
    let date = new Date(this.getTime());
    date.setFullYear(this.getFullYear() + years);
    return date;
  }
}

interface String {
  /** Converts string to base64 format */
  base64(): string;

  /**
   * gets measurement of current string in a specified font.
   * @param font css font property value
   */
  measure(font: string): TextMetrics;
}

{
  String.prototype.base64 = function (this: string): string {
    return btoa(encodeURIComponent(this).replace(/%([0-9A-F]{2})/g, (m, p) => String.fromCharCode(('0x' + p) as any)));
  }

  const measurer = document.createElement('canvas');
  const context = measurer.getContext('2d');

  String.prototype.measure = function (this: string, font: string): TextMetrics {
    
    context.font = font;
    return context.measureText(this);
  }
}

interface HTMLElement {

  /** return nearest scrollable element of current element (the element itself or one of its parent) */
  scrollableElement(): HTMLElement;

  /** return nearest dispatcher of scroll event of current element (the element itself or one of its parent) */
  scrollEventDispatcher(): HTMLElement | Window;

  /** gets element on the top of current element's hierachy*/
  topElement(): HTMLElement;
}

{
  const unscrollableStates = ['hidden', 'visible'];

  function scrollable(element: HTMLElement) {
    let info = window.getComputedStyle(element);
    return !unscrollableStates.contains(info.overflowY) || !unscrollableStates.contains(info.overflowX);
  }

  HTMLElement.prototype.scrollableElement = function (this: HTMLElement): HTMLElement {

    let candidate = this;
    while (candidate && !scrollable(candidate)) candidate = candidate.parentElement;
    return candidate ? candidate : document.body.parentElement;
  }

  HTMLElement.prototype.scrollEventDispatcher = function (this: HTMLElement): HTMLElement | Window {
    let container: HTMLElement = this.scrollableElement();
    return container == document.body.parentElement ? window : container;    
  }

  HTMLElement.prototype.topElement = function (this: HTMLElement): HTMLElement {

    let candidate = this;
    while (candidate.parentElement) candidate = candidate.parentElement;
    return candidate;
  } 
}

interface Window {
  getBoundingClientRect(): DOMRect;
}

{
  Window.prototype.getBoundingClientRect = function (): DOMRect {
    return new DOMRect(0, 0, innerWidth, innerHeight);
  }
}

interface Element {
  /**
 * gets nearest element with a specified selector of the current element.
 * @param selector selector to be searched.
 * @param search type of hierachy to be searched. If not provided, element will be searched in current element's ancestors.
 * @param type type of element to be searched. If not provied, nearest satisfied element will be retrived. This parameter only have effect for sibling hierachy.
 */
  nearest(selector: string, search?: 'ancestor' | 'children' | 'sibling', type?: 'younger' | 'older' | 'closest'): Element;

  /**
   * observes element's size / content for changes.
   * @param type type of observing feature
   * @param callback callback to be invoked when the observed feature of the element is changed. If this method return false the feature will be stopped monitoring.
   */
  observe(type: 'content' | 'size', callback: (entries: MutationRecord[] | ResizeObserverEntry[], observer?: MutationObserver | ResizeObserver) => boolean): MutationObserver | ResizeObserver;
}

{
  Element.prototype.nearest = function (this: Element, selector: string, search?: 'ancestor' | 'children' | 'sibling', type?: 'younger' | 'older' | 'closest'): Element {
    if (!search) search = 'ancestor';
    if (!type) type = 'closest';

    switch (search) {
      case 'ancestor': return this.closest(selector);
      case 'children': return this.querySelector(selector);

      case 'sibling':

        switch (type) {
          case 'younger':

            {
              let candidate = this.previousElementSibling;
              while (candidate && !candidate.matches(selector)) candidate = candidate.previousElementSibling;
              return candidate;
            }

          case 'older':

            {
              let candidate = this.nextElementSibling;
              while (candidate && !candidate.matches(selector)) candidate = candidate.nextElementSibling;
              return candidate;
            }

          case 'closest':
            {
              let older = this;
              let younger = this;

              do {

                older = older.previousElementSibling;
                if (older.matches(selector)) return older;

                younger = younger.nextElementSibling;
                if (younger.matches(selector)) return younger;
              }
              while (older || younger);

              return undefined;
            }
        }
    }
  }

  Element.prototype.observe = function (this: Element, type: 'content' | 'size', callback: (entries: MutationRecord[] | ResizeObserverEntry[], observer?: MutationObserver | ResizeObserver) => boolean): MutationObserver | ResizeObserver {

    switch (type) {
      case 'content': {
        let o = new MutationObserver(entries => {
          if (callback(entries, o) == false) o.disconnect();
        });

        o.observe(this, { subtree: true, characterData: true });
        return o;
      }

      case 'size': {
        let o = new ResizeObserver(entries => {
          if (callback(entries, o) == false) o.disconnect();
        });

        o.observe(this);
        return o;
      }
    }
  }
}
